import { useCallback, useEffect } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { RawMediaCropInput } from 'src/app/shared/components/imageUpload';
import { useImageUploadContext } from 'src/app/shared/hooks/imageUpload';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

import { IMAGE_UPLOAD_ON_FILE_ADDED_EVENT, SUGGESTED_TEMPLATE_NOTICE } from '../../../../../../constants';
import { useCreatePetitionUpdateContext } from '../../../../context';
import { useLocalStoragePetitionUpdateMedia } from '../../../../hooks/useLocalStorage';
import type { UseImageUploadContainerProps, UseImageUploadContainerResult } from '../../../../types';

export function useImageUploadContainer({
	uploader,
	initialImage,
}: UseImageUploadContainerProps): UseImageUploadContainerResult {
	const {
		state: { image, imageCrop, errorMessage },
		dispatch,
	} = useImageUploadContext();
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const isTemplateFilled = useCreatePetitionUpdateContext();
	const sourceLocation = useQueryParamValue('source_location');
	const {
		data: {
			petition: { id },
		},
	} = useStarterDashboardV2PageData();
	const { petitionUpdateMedia, removePetitionUpdateMedia } = useLocalStoragePetitionUpdateMedia(id, isTemplateFilled);

	useEffect(() => {
		if (petitionUpdateMedia.media?.data && petitionUpdateMedia.media.crop) {
			dispatch({ type: 'SET_IMAGE', payload: { image: petitionUpdateMedia.media.data, uploader } });
			dispatch({ type: 'SET_IMAGE_CROP', payload: { img: petitionUpdateMedia.media.crop } });
		}
		// Set image & crop on initial render if they exist
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onImageChange = useCallback(
		(uploadedImage?: string) => {
			if (uploadedImage === undefined) {
				removePetitionUpdateMedia();
			}
			dispatch({ type: 'SET_IMAGE', payload: { image: uploadedImage, uploader } });
		},
		[dispatch, removePetitionUpdateMedia, uploader],
	);

	const onImageCrop = useCallback(
		(img?: RawMediaCropInput) => dispatch({ type: 'SET_IMAGE_CROP', payload: { img } }),
		[dispatch],
	);

	useEffect(() => {
		if (initialImage) {
			dispatch({
				type: 'SET_IMAGE',
				payload: {
					image: initialImage,
					uploader,
					initialLoad: true,
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		uploader.onFileAdded(onImageChange);
		uploader.onFileAddedTracking(({ uploadMethod, fileType }) => {
			void track(IMAGE_UPLOAD_ON_FILE_ADDED_EVENT, {
				petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
				source_location: sourceLocation,
				upload_method: uploadMethod,
				file_type: fileType,
				victory: false,
				edit: false,
			});
		});
		uploader.onUseDropTarget();
		uploader.onUseAwsS3(utilityContext, 'USER_UPLOADS');

		// No need to pass uploader in dependency array.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [utilityContext]);

	return {
		data: {
			image,
			imageCrop,
			errorMessage,
		},
		actions: {
			onImageChange,
			onImageCrop,
			dispatch,
		},
	};
}
